import { useEffect, useRef } from 'react'
import { faTriangleExclamation, faXmark } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useLiveQuery } from 'dexie-react-hooks'
import { useSnackbar } from 'notistack'
import { offlineDb, type SyncIssue } from '../../offline/db'
import { SyncStatus } from '../../DTOs/Sync/SyncDtos'
import { useGlobalModal } from '../Contexts/GlobalModalContext'
import Button from '../Buttons/Button'
import ConflictResolutionModal from './ConflictResolutionModal'

function issueLabel(issue: SyncIssue): string {
  const entityName = issue.entity === 'diggingPattern' ? 'digging location'
    : issue.entity === 'patternBlast' ? 'blast'
    : issue.entity === 'loading' ? 'truck load'
    : issue.entity === 'geoRehandle' ? 'rehandle/crushing entry'
    : issue.entity === 'completeShift' ? 'shift completion'
    : issue.entity
  if (issue.status === SyncStatus.Conflict) return `A ${entityName} conflict: it was edited by someone else`
  if (issue.status === SyncStatus.ShiftLocked) return `A ${entityName} change was rejected: shift is locked`
  if (issue.status === SyncStatus.Error) return `A ${entityName} change failed: ${typeof issue.serverState === 'string' ? issue.serverState : 'server error'}`
  return `A ${entityName} change could not be applied: record not found`
}

async function dismissIssue(id: number) {
  await offlineDb.syncIssues.delete(id)
}

async function dismissAll() {
  await offlineDb.syncIssues.clear()
}

const SyncIssuesBanner = () => {
  const issuesRaw = useLiveQuery(() => offlineDb.syncIssues.orderBy('createdAt').toArray(), [])
  const issues = issuesRaw ?? []
  const { showModal, hideModal } = useGlobalModal()
  const { enqueueSnackbar } = useSnackbar()

  // Note: raise a transient notification when new sync issues are recorded, so a conflict or
  // rejected change is not silent (the banner alone is easy to miss). Skip the first Dexie
  // emission so pre-existing issues restored on load do not fire a notification.
  const prevCountRef = useRef<number>(-1)
  useEffect(() => {
    if (issuesRaw === undefined) return
    const prev = prevCountRef.current
    if (prev >= 0 && issuesRaw.length > prev) {
      const added = issuesRaw.length - prev
      enqueueSnackbar(
        `${added} offline change${added === 1 ? '' : 's'} need${added === 1 ? 's' : ''} your attention. See the sync issues bar at the top of the screen.`,
        { variant: 'warning', autoHideDuration: 8000 }
      )
    }
    prevCountRef.current = issuesRaw.length
  }, [issuesRaw, enqueueSnackbar])

  const openConflictModal = (issue: SyncIssue) => {
    showModal({
      content: () => (
        <ConflictResolutionModal issue={issue} onClose={hideModal} />
      ),
      tailwindMaxWidthClass: 'max-w-2xl',
    })
  }

  if (issues.length === 0) return null

  return (
    <div className="w-full flex flex-col gap-1 px-4 py-2 short:py-1 bg-theme-danger text-theme-primary-contrast text-sm border-b border-theme-danger-hover shrink-0">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 font-medium">
          <FontAwesomeIcon icon={faTriangleExclamation} className="shrink-0" />
          <span>{issues.length} sync {issues.length === 1 ? 'issue' : 'issues'} need your attention</span>
        </div>
        <Button variant="ghost" size="sm" onClick={dismissAll} className="shrink-0 text-white hover:bg-theme-danger-hover">
          Dismiss all
        </Button>
      </div>
      <ul className="flex flex-col gap-0.5 pl-6">
        {issues.map(issue => (
          <li key={issue.id} className="flex items-center justify-between gap-2">
            <span className="text-xs opacity-90">{issueLabel(issue)}</span>
            <div className="flex items-center gap-2 shrink-0">
              {issue.status === SyncStatus.Conflict && (
                <Button variant="secondary" size="sm" onClick={() => openConflictModal(issue)}>
                  Resolve
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => dismissIssue(issue.id!)}
                aria-label="Dismiss"
                leftIcon={faXmark}
                className="text-white hover:bg-theme-danger-hover"
              />
            </div>
          </li>
        ))}
      </ul>
    </div>
  )
}

export default SyncIssuesBanner
